import {
  CONSTRUCTION_TYPES,
  CONSERVATION_STATES,
  LAND_USES,
  DOCUMENT_TYPES,
  NOTARIES,
  LAND_VALUE_STREETS
} from '../data/catalogs.constants';
import { PROPERTY_REGISTRY_INDEX } from '../data/cadastral-registry.data';

const currencyFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2
});

const numberFormatter = new Intl.NumberFormat('es-MX', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const integerFormatter = new Intl.NumberFormat('es-MX');

export const formatMoney = value => currencyFormatter.format(value || 0);
export const formatNum = value => numberFormatter.format(value || 0);
export const formatInt = value => integerFormatter.format(value || 0);

export const formatPeriod = periodCode =>
  periodCode && periodCode.length === 6 ? `${periodCode.slice(4)}/${periodCode.slice(0, 4)}` : '—';

function seedFromKey(recordKey) {
  let h = 2166136261 >>> 0;
  for (let index = 0; index < recordKey.length; index++) {
    h ^= recordKey.charCodeAt(index);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function createRng(seed) {
  let state = seed >>> 0;
  return function () {
    state = (state + 0x6D2B79F5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

const roundToTwoDecimals = value => Math.round(value * 100) / 100;

export function generateRecordDetails(record) {
  if (!record) return null;

  const randomValue = createRng(seedFromKey(record.clave));
  const pick = items => items[Math.floor(randomValue() * items.length)];
  const range = (minimum, maximum, decimals) => {
    const value = minimum + randomValue() * (maximum - minimum);
    return decimals === 0 ? Math.round(value) : roundToTwoDecimals(value);
  };

  const classification = record.clasificacion;
  const isRural = classification === 'Predio Rústico';
  const isVacant = classification === 'Predio Urbano Baldío';
  const isBuilt = classification === 'Predio Urbano Edificado';

  /* A) Land */
  let landArea, landUnitValue;
  if (isRural) {
    landArea = range(1800, 42000, 2);
    landUnitValue = range(180, 920, 0);
  } else if (isVacant) {
    landArea = range(110, 880, 2);
    landUnitValue = range(1200, 4900, 0);
  } else {
    landArea = range(92, 640, 2);
    landUnitValue = range(2400, 9800, 0);
  }
  landUnitValue = Math.round(landUnitValue / 10) * 10;

  const meritFactor = randomValue() < 0.62 ? 1.0 : roundToTwoDecimals(range(0.82, 1.18, 2));
  const landValue = roundToTwoDecimals(landArea * landUnitValue * meritFactor);
  const frontage = roundToTwoDecimals(Math.sqrt(landArea) * range(0.55, 0.95, 2));
  const depth = roundToTwoDecimals(landArea / frontage);

  const landValueCode = {
    code: record.clave.slice(0, 9) + String(range(1, 99, 0)).padStart(2, '0'),
    stretch: `${pick(LAND_VALUE_STREETS)} / ${pick(LAND_VALUE_STREETS)} / ${pick(LAND_VALUE_STREETS)}`,
    unitValue: landUnitValue
  };

  /* B) Constructions */
  const constructionRecords = [];
  const modernConstructionTypes = ['1416', '1417', '1418', '1419', '1420', '1421', '1422'];
  const olderConstructionTypes = ['1409', '1410', '1411', '1412', '1413'];
  const industrialConstructionTypes = ['1402', '1403', '1404', '1405', '1406'];

  if (isBuilt) {
    const levelCount = randomValue() < 0.42 ? 1 : randomValue() < 0.72 ? 2 : 3;
    const constructionTypePool = randomValue() < 0.16
      ? olderConstructionTypes
      : randomValue() < 0.12 ? industrialConstructionTypes : modernConstructionTypes;
    for (let level = 1; level <= levelCount; level++) {
      const recordCount = level === 1 ? (randomValue() < 0.55 ? 2 : 1) : 1;
      for (let recordIndex = 0; recordIndex < recordCount; recordIndex++) {
        const constructionType = pick(constructionTypePool);
        const condition = randomValue() < 0.34 ? 1 : randomValue() < 0.55 ? 2 : randomValue() < 0.78 ? 3 : randomValue() < 0.93 ? 4 : 5;
        const surfaceArea = roundToTwoDecimals((landArea * range(0.16, 0.62, 2)) / (level * (recordIndex + 1)));
        const unitValue = CONSTRUCTION_TYPES[constructionType]?.vu || 3500;
        const conditionFactor = CONSERVATION_STATES[condition]?.factor || 1.0;
        constructionRecords.push({
          level,
          type: constructionType,
          status: condition,
          area: surfaceArea,
          unitValue,
          factor: conditionFactor,
          totalValue: roundToTwoDecimals(surfaceArea * unitValue * conditionFactor)
        });
      }
    }
    /* Amenities */
    if (randomValue() < 0.14) {
      const amenityType = randomValue() < 0.6 ? '1423' : '1424';
      const amenityArea = roundToTwoDecimals(range(18, 72, 2));
      const amenityUnitValue = CONSTRUCTION_TYPES[amenityType].vu;
      constructionRecords.push({
        level: 1,
        type: amenityType,
        status: 2,
        area: amenityArea,
        unitValue: amenityUnitValue,
        factor: 0.9,
        totalValue: roundToTwoDecimals(amenityArea * amenityUnitValue * 0.9)
      });
    }
  } else if (isRural && randomValue() < 0.34) {
    const surfaceArea = roundToTwoDecimals(range(24, 130, 2));
    const unitValue = CONSTRUCTION_TYPES['1401'].vu;
    const condition = randomValue() < 0.5 ? 3 : 4;
    const conditionFactor = CONSERVATION_STATES[condition].factor;
    constructionRecords.push({
      level: 1,
      type: '1401',
      status: condition,
      area: surfaceArea,
      unitValue,
      factor: conditionFactor,
      totalValue: roundToTwoDecimals(surfaceArea * unitValue * conditionFactor)
    });
  }

  const constructionArea = roundToTwoDecimals(constructionRecords.reduce((total, construction) => total + construction.area, 0));
  const constructionValue = roundToTwoDecimals(constructionRecords.reduce((total, construction) => total + construction.totalValue, 0));
  const cadastralValue = roundToTwoDecimals(landValue + constructionValue);

  /* C) Previous and updated values */
  const isUpdated = isBuilt && randomValue() < 0.55;
  let previousRecord = null;
  if (isUpdated) {
    const previousLandArea = roundToTwoDecimals(landArea * range(1.05, 1.9, 2));
    const previousLandUnitValue = Math.round((landUnitValue * range(0.52, 0.85, 2)) / 10) * 10;
    const previousConstructionArea = roundToTwoDecimals(constructionArea * range(0.1, 0.55, 2));
    const previousLandValue = roundToTwoDecimals(previousLandArea * previousLandUnitValue);
    const previousConstructionValue = roundToTwoDecimals(previousConstructionArea * CONSTRUCTION_TYPES['1402'].vu);
    previousRecord = {
      landArea: previousLandArea,
      landUnitValue: previousLandUnitValue,
      landValue: previousLandValue,
      buildingArea: previousConstructionArea,
      buildingValue: previousConstructionValue,
      cadastralValue: roundToTwoDecimals(previousLandValue + previousConstructionValue)
    };
  }

  /* D) Registry and deed */
  const acquisitionYear = parseInt(record.anio || '2005', 10) || 2005;
  const deedYear = Math.max(1968, acquisitionYear - Math.round(range(0, 22, 0)));
  const monthNames = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];
  const notary = pick(NOTARIES);

  const deed = {
    type: pick(DOCUMENT_TYPES),
    number: String(range(120, 9800, 0)),
    volume: ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'][range(0, 6, 0)],
    date: `${range(1, 28, 0)} de ${monthNames[range(0, 11, 0)]} de ${deedYear}`,
    notaryName: notary.nom,
    notaryOffice: notary.n,
    location: notary.lugar,
    publicRegistry: `Partida ${range(3, 480, 0)}, Tomo ${['VIII', 'XI', 'XVI', 'XXII', 'XXXI', 'XLII'][range(0, 5, 0)]}, Libro ${range(12, 210, 0)}, primera sección`,
    realEstateFolio: `FR-${String(range(100000, 999999, 0))}`
  };

  const constructionYear = constructionRecords.length
    ? Math.max(1955, acquisitionYear - Math.round(range(0, 30, 0)))
    : null;

  const registry = {
    registrationDate: `${range(1, 28, 0)}/${String(range(1, 12, 0)).padStart(2, '0')}/${acquisitionYear}`,
    lastUpdateDate: `${range(1, 28, 0)}/${String(range(1, 12, 0)).padStart(2, '0')}/${randomValue() < 0.5 ? 2025 : 2026}`,
    fiscalYear: '2026',
    regime: isRural
      ? randomValue() < 0.45 ? 'Propiedad ejidal' : 'Propiedad privada'
      : randomValue() < 0.72 ? 'Propiedad privada' : 'Régimen en condominio',
    landUse: isRural ? 'Agropecuario' : isVacant ? 'Habitacional' : pick(LAND_USES),
    zone: `Z-${String(range(1, 28, 0)).padStart(2, '0')}`,
    block: record.clave.slice(7, 9),
    lot: record.clave.slice(9, 12),
    unit: record.clave.slice(12, 15),
    sector: record.clave.slice(0, 9),
    constructionYear,
    account: `CP-${record.clave.slice(2, 10)}-${record.clave.slice(-3)}`
  };

  /* E) Services */
  const serviceProbability = isRural ? 0.28 : isVacant ? 0.6 : 0.92;
  const serviceAvailability = {
    'Agua potable': randomValue() < serviceProbability,
    'Drenaje sanitario': randomValue() < serviceProbability,
    'Energía eléctrica': randomValue() < serviceProbability + 0.06,
    'Alumbrado público': randomValue() < serviceProbability - 0.05,
    'Pavimento': randomValue() < serviceProbability - 0.12,
    'Banqueta': randomValue() < serviceProbability - 0.15,
    'Recolección': randomValue() < serviceProbability - 0.08,
    'Alcantarillado': randomValue() < serviceProbability - 0.2
  };

  /* F) Polygon */
  let polygon = null;
  if (record.lat && record.lon) {
    const gridRadius = Math.sqrt(landArea) / 111320;
    const width = gridRadius * range(0.7, 1.05, 2);
    const height = gridRadius * range(0.7, 1.05, 2);
    const rotation = randomValue() * Math.PI;
    const cosRotation = Math.cos(rotation);
    const sinRotation = Math.sin(rotation);
    const corners = [[-width, -height], [width, -height], [width, height], [-width, height]];
    polygon = corners.map(([offsetX, offsetY]) => [
      record.lat + (offsetX * sinRotation + offsetY * cosRotation),
      record.lon + (offsetX * cosRotation - offsetY * sinRotation) / Math.cos((record.lat * Math.PI) / 180)
    ]);
  }

  /* G) Tax status */
  const outstandingPeriods = (() => {
    if (!record.periodoIni || !record.periodoFin) return 0;
    const initialYear = +record.periodoIni.slice(0, 4);
    const initialBimonth = +record.periodoIni.slice(4);
    const finalYear = +record.periodoFin.slice(0, 4);
    const finalBimonth = +record.periodoFin.slice(4);
    return Math.max(0, (finalYear - initialYear) * 6 + (finalBimonth - initialBimonth));
  })();

  return {
    land: {
      area: landArea,
      unitValue: landUnitValue,
      meritFactor,
      totalValue: landValue,
      frontage,
      depth,
      valueCode: landValueCode
    },
    buildings: constructionRecords,
    totalBuildingArea: constructionArea,
    totalBuildingValue: constructionValue,
    cadastralValue,
    previousAssessment: previousRecord,
    isUpdated,
    deed,
    registry,
    services: serviceAvailability,
    polygon,
    fiscal: {
      startPeriod: formatPeriod(record.periodoIni),
      endPeriod: formatPeriod(record.periodoFin),
      bimonthlyPeriods: outstandingPeriods,
      debtAmount: record.adeudo,
      debtText: record.adeudoTexto
    }
  };
}

export function validateCadastralKey(cadastralKey) {
  if (!cadastralKey || !cadastralKey.trim()) {
    return {
      valido: false,
      titulo: 'Captura una clave catastral',
      mensaje: 'Ingresa los 15 dígitos de la clave para localizar el predio en el mapa.'
    };
  }
  const cleanKey = cadastralKey.trim();
  if (!/^\d{15}$/.test(cleanKey)) {
    return {
      valido: false,
      titulo: 'Clave catastral inválida',
      mensaje: cleanKey.length < 15
        ? `La clave capturada tiene ${cleanKey.length} dígitos y se requieren 15.`
        : 'La clave contiene caracteres no numéricos. Estructura: 14 municipio · 01 región · 001 sector · 01 manzana · 000 predio · 000 unidad.'
    };
  }
  return { valido: true, clave: cleanKey };
}

export function findLocalProperty(cadastralKey) {
  const localRecord = PROPERTY_REGISTRY_INDEX[cadastralKey];
  if (!localRecord) return null;

  const record = {
    propertyKey: localRecord.clave,
    delegation: localRecord.delegacion,
    ownerName: localRecord.propietario,
    address: localRecord.direccion,
    fiscalAddress: localRecord.domFiscal,
    rfc: localRecord.rfc,
    neighborhood: localRecord.colonia,
    classification: localRecord.clasificacion,
    startPeriod: localRecord.periodoIni,
    endPeriod: localRecord.periodoFin,
    debtAmount: localRecord.adeudo,
    debtText: localRecord.adeudoTexto,
    entityType: localRecord.tipoPersona,
    taxYear: localRecord.anio,
    latitude: localRecord.lat,
    longitude: localRecord.lon
  };

  return { record, file: generateRecordDetails(localRecord) };
}
